import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, formatDate, formatRelative, ROLE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import PermissionMatrix from "@/components/PermissionMatrix";
import NotificationPrefsPanel from "@/components/NotificationPrefsPanel";
import { Plus, X, Users as UsersIcon, ShieldCheck, KeyRound, Copy, RefreshCw, Check, Pencil, Bell, Activity, FileText, ClipboardList, Share2, CalendarClock, HandHeart, Ban, Power } from "lucide-react";

// Static fallback used only for label rendering + envs where /admin/roles
// hasn't seeded yet. The dropdowns are populated from the live roles API
// so admins can assign custom roles they created in Admin → Roles.
const FALLBACK_ROLES = Object.keys(ROLE_LABELS);
const roleLabel = (key, staffRoles) => {
  const dyn = (staffRoles || []).find((r) => r.key === key);
  return dyn?.name || ROLE_LABELS[key] || key;
};

function generateSecurePassword() {
  // 12 chars: upper, lower, digits, symbols — avoids ambiguous chars
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%^&*";
  const all = upper + lower + digits + symbols;
  const get = (set) => set[Math.floor(Math.random() * set.length)];
  const chars = [get(upper), get(lower), get(digits), get(symbols)];
  for (let i = 0; i < 8; i++) chars.push(get(all));
  return chars.sort(() => Math.random() - 0.5).join("");
}

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [staffRoles, setStaffRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [tab, setTab] = useState(() => {
    if (typeof window !== "undefined") {
      const t = new URLSearchParams(window.location.search).get("tab");
      // 'volunteers' is no longer a top-level tab — it lives inside People.
      if (t === "volunteers") return "people";
      if (t && ["people", "permissions", "activity", "notifications"].includes(t)) return t;
    }
    return "people";
  }); // 'people' | 'permissions' | 'activity' | 'notifications'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users", { params: { include_inactive: true } });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Load every staff-scope role (system + custom) so the dropdowns include
  // roles admins created in Admin → Roles. Only fetched when the caller
  // can manage users; otherwise the selects aren't rendered anyway.
  useEffect(() => {
    const canManage =
      currentUser?.role === "admin" || currentUser?.role === "executive_director";
    if (!canManage) return;
    api.get("/admin/roles", { params: { scope: "staff" } })
      .then(({ data }) => setStaffRoles(data.roles || []))
      .catch(() => setStaffRoles([]));
  }, [currentUser?.role]);

  const roleOptions = staffRoles.length
    ? staffRoles.map((r) => ({ key: r.key, name: r.name }))
    : FALLBACK_ROLES.map((k) => ({ key: k, name: ROLE_LABELS[k] }));

  const updateRole = async (uid, role) => {
    try {
      await api.patch(`/users/${uid}`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "User disabled" : "User enabled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleResetMfa = async (u) => {
    const ok = window.confirm(
      `Reset MFA for ${u.name || u.email}?\n\n` +
      `They will be required to re-enroll their authenticator app on their next sign-in. ` +
      `All their trusted devices will be invalidated.`
    );
    if (!ok) return;
    try {
      await api.post(`/auth/mfa/reset/${u.id}`);
      toast.success(`MFA reset for ${u.name || u.email}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleUnlock = async (u) => {
    // Clears the brute-force lockout counters for this user so they can
    // sign in immediately instead of waiting 15 minutes. Only admins /
    // EDs see this button.
    try {
      const { data } = await api.post(`/auth/unlock/${u.id}`);
      toast.success(
        data.counters_cleared > 0
          ? `${u.name || u.email} unlocked — they can sign in now.`
          : `No active lockout for ${u.name || u.email}.`
      );
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const isAdmin = currentUser?.role === "admin";
  const canManageUsers = currentUser?.role === "admin" || currentUser?.role === "executive_director";

  // Lookup: user id → display name (used by the Supervisor column)
  const usersById = users.reduce((acc, u) => {
    acc[u.id] = u.name;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Your team</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Team Members</h1>
        </div>
        {isAdmin && tab === "people" && (
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium" data-testid="add-user-btn">
            <Plus className="w-4 h-4" /> Add member
          </button>
        )}
      </div>

      <div className="inline-flex p-1 rounded-full bg-white border border-[#E5E5E0]" data-testid="users-tabs">
        <button
          onClick={() => setTab("people")}
          data-testid="users-tab-people"
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
            tab === "people" ? "bg-[#1E3A8A] text-white" : "text-[#1E3A8A]"
          }`}
        >
          <UsersIcon className="w-3.5 h-3.5" /> People
        </button>
        <button
          onClick={() => setTab("permissions")}
          data-testid="users-tab-permissions"
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
            tab === "permissions" ? "bg-[#1E3A8A] text-white" : "text-[#1E3A8A]"
          }`}
        >
          <ShieldCheck className="w-3.5 h-3.5" /> Permissions
        </button>
        <button
          onClick={() => setTab("activity")}
          data-testid="users-tab-activity"
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
            tab === "activity" ? "bg-[#1E3A8A] text-white" : "text-[#1E3A8A]"
          }`}
        >
          <Activity className="w-3.5 h-3.5" /> Activity
        </button>
        <button
          onClick={() => setTab("notifications")}
          data-testid="users-tab-notifications"
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
            tab === "notifications" ? "bg-[#1E3A8A] text-white" : "text-[#1E3A8A]"
          }`}
        >
          <Bell className="w-3.5 h-3.5" /> My notifications
        </button>
      </div>

      {tab === "permissions" ? (
        <PermissionMatrix />
      ) : tab === "activity" ? (
        <ActivityPanel />
      ) : tab === "notifications" ? (
        <NotificationPrefsPanel />
      ) : loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : (
        <>
          {/* Mobile: card list */}
          <ul className="md:hidden space-y-3" data-testid="users-list-mobile">
            {users.map((u) => (
              <li key={u.id} className="bg-white border border-[#E5E5E0] rounded-2xl p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {u.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#0F172A] truncate">{u.name}</div>
                    {u.title && <div className="text-xs text-[#334155] truncate">{u.title}</div>}
                    <div className="text-xs text-[#1E3A8A] truncate">{u.email}</div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider ${u.active ? "bg-[#E8ECE7] text-[#166534]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                    {u.active ? "Active" : "Disabled"}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-[#F0EFEB]">
                  <span className="text-xs text-[#1E3A8A]">Role</span>
                  {isAdmin && u.id !== currentUser?.id ? (
                    <select value={u.role} onChange={(e) => updateRole(u.id, e.target.value)} className="px-2 py-1 rounded-lg border border-[#E5E5E0] text-xs" data-testid={`role-select-mobile-${u.id}`}>
                      {roleOptions.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                    </select>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-[#334155]">
                      {u.role === "admin" && <ShieldCheck className="w-3 h-3 text-[#1E3A8A]" />}
                      {roleLabel(u.role, staffRoles)}
                    </span>
                  )}
                </div>
                {canManageUsers && u.id !== currentUser?.id && !(u.role === "admin" && !isAdmin) && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditTarget(u)}
                      data-testid={`edit-user-mobile-${u.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-xs font-medium text-[#1E3A8A]"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
                      Edit profile
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => setResetTarget(u)}
                          data-testid={`reset-password-mobile-${u.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-xs font-medium text-[#1E3A8A]"
                        >
                          <KeyRound className="w-3.5 h-3.5" strokeWidth={2} />
                          Reset password
                        </button>
                        <button
                          onClick={() => toggleActive(u)}
                          className={`flex-1 inline-flex items-center justify-center px-3 py-2 rounded-full text-xs font-medium ${u.active ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#E8ECE7] text-[#166534]"}`}
                        >
                          {u.active ? "Disable" : "Enable"}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* Desktop: table */}
          <div className="hidden md:block bg-white border border-[#E5E5E0] rounded-2xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-[#F9F8F6] border-b border-[#E5E5E0]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Name</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Role</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden lg:table-cell">Supervisor</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden md:table-cell">Joined</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Status</th>
                {canManageUsers && <th className="text-right px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-[#F0EFEB]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center text-xs font-semibold">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-[#0F172A] inline-flex items-center gap-1.5">
                          {u.name}
                          {u.mfa_enabled && (
                            <span
                              title="Two-factor authentication enabled"
                              data-testid={`mfa-badge-${u.id}`}
                              className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-[#E0E7FF] text-[#1E40AF]"
                            >
                              <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2.5} /> MFA
                            </span>
                          )}
                        </div>
                        {u.title && <div className="text-xs text-[#1E3A8A]">{u.title}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#334155] hidden sm:table-cell">{u.email}</td>
                  <td className="px-4 py-3">
                    {isAdmin && u.id !== currentUser?.id ? (
                      <select value={u.role} onChange={(e) => updateRole(u.id, e.target.value)} className="px-2 py-1 rounded-lg border border-[#E5E5E0] text-xs" data-testid={`role-select-${u.id}`}>
                        {roleOptions.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                      </select>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-[#334155]">
                        {u.role === "admin" && <ShieldCheck className="w-3 h-3 text-[#1E3A8A]" />}
                        {roleLabel(u.role, staffRoles)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-[#334155]" data-testid={`supervisor-${u.id}`}>
                    {u.supervisor_id ? (usersById[u.supervisor_id] || "—") : <span className="text-[#92400E]">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3 text-[#1E3A8A] hidden md:table-cell">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    {isAdmin && u.id !== currentUser?.id ? (
                      <button onClick={() => toggleActive(u)} className={`px-2 py-1 rounded-full text-[10px] uppercase tracking-wider ${u.active ? "bg-[#E8ECE7] text-[#166534]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                        {u.active ? "Active" : "Disabled"}
                      </button>
                    ) : (
                      <span className={`px-2 py-1 rounded-full text-[10px] uppercase tracking-wider ${u.active ? "bg-[#E8ECE7] text-[#166534]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                        {u.active ? "Active" : "Disabled"}
                      </span>
                    )}
                  </td>
                  {canManageUsers && (
                    <td className="px-4 py-3 text-right">
                      {u.id !== currentUser?.id ? (
                        u.role === "admin" && !isAdmin ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] text-[#1E3A8A]"
                            data-testid={`admin-readonly-${u.id}`}
                            title="Only an admin can edit another admin account"
                          >
                            <ShieldCheck className="w-3 h-3" strokeWidth={2} />
                            Admin · view only
                          </span>
                        ) : (
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => setEditTarget(u)}
                            data-testid={`edit-user-${u.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-[11px] font-medium text-[#1E3A8A] hover:bg-[#E8ECE7] transition-colors"
                          >
                            <Pencil className="w-3 h-3" strokeWidth={2} />
                            Edit profile
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => setResetTarget(u)}
                                data-testid={`reset-password-${u.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-[11px] font-medium text-[#1E3A8A] hover:bg-[#E8ECE7] transition-colors"
                              >
                                <KeyRound className="w-3 h-3" strokeWidth={2} />
                                Reset password
                              </button>
                              {u.mfa_enabled && (
                                <button
                                  onClick={() => handleResetMfa(u)}
                                  data-testid={`reset-mfa-${u.id}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-[11px] font-medium text-[#B45309] hover:bg-[#FEF3C7] transition-colors"
                                  title="Force this user to re-enroll their authenticator on next sign-in"
                                >
                                  <ShieldCheck className="w-3 h-3" strokeWidth={2} />
                                  Reset MFA
                                </button>
                              )}
                              <button
                                onClick={() => handleUnlock(u)}
                                data-testid={`unlock-user-${u.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-[11px] font-medium text-[#166534] hover:bg-[#D1FAE5] transition-colors"
                                title="Clear brute-force lockout so this user can sign in immediately"
                              >
                                <Power className="w-3 h-3" strokeWidth={2} />
                                Unlock
                              </button>
                            </>
                          )}
                        </div>
                        )
                      ) : (
                        <span className="text-[10px] text-[#1E3A8A]">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {canManageUsers && (
          <section id="volunteer-accounts" className="space-y-3 pt-2 scroll-mt-24" data-testid="users-volunteers-section">
            <div className="flex items-end justify-between gap-3 flex-wrap border-t border-[#E5E5E0] pt-6">
              <div>
                <div className="hw-eyebrow mb-1">Community</div>
                <h2 className="font-display text-xl font-semibold text-[#0F172A]">Volunteer accounts</h2>
                <p className="text-xs text-[#334155] mt-1">
                  Reset passwords, disable or re-enable sign-in. New signups and hours review still live on the <span className="text-[#1E3A8A]">Volunteers</span> page.
                </p>
              </div>
            </div>
            <VolunteerAccountsPanel />
          </section>
        )}
        </>
      )}

      {showForm && <UserForm roleOptions={roleOptions} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {resetTarget && <ResetPasswordDialog targetUser={resetTarget} onClose={() => setResetTarget(null)} />}
      {editTarget && (
        <UserEditDialog
          targetUser={editTarget}
          roleOptions={roleOptions}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function UserForm({ onClose, onCreated, roleOptions }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "field_specialist", title: "", phone: "", active: true, supervisor_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    // Load other staff so the new member can be assigned a supervisor on
    // creation (saves an edit step). Volunteers are excluded.
    api.get("/users").then((r) => {
      setTeamMembers((r.data || []).filter((u) => u.active !== false && u.role !== "volunteer"));
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.title) delete payload.title;
      if (!payload.supervisor_id) delete payload.supervisor_id;
      await api.post("/users", payload);
      toast.success("Team member added");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="border-b border-[#E5E5E0] px-6 py-4 flex justify-between">
          <h2 className="font-display text-xl font-semibold">Add Team Member</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="user-form">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="user-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Title <span className="text-xs text-[#1E3A8A] font-normal">(optional, e.g. "Community Wellness Field Specialist — Wayne County")</span></label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="user-title" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="user-email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Password</label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="user-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Role <span className="text-xs text-[#1E3A8A] font-normal">(controls permissions)</span></label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="user-role">
              {(roleOptions && roleOptions.length ? roleOptions : FALLBACK_ROLES.map((k) => ({ key: k, name: ROLE_LABELS[k] })))
                .map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">
              Supervisor <span className="text-xs text-[#1E3A8A] font-normal">(reviews timesheets & approves time)</span>
            </label>
            <select
              value={form.supervisor_id}
              onChange={(e) => setForm({ ...form, supervisor_id: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              data-testid="user-create-supervisor"
            >
              <option value="">— None (admins review) —</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} {m.title ? `· ${m.title}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Phone</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="user-submit-btn" className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium disabled:opacity-60">
              {submitting ? "Adding…" : "Add member"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserEditDialog({ targetUser, onClose, onSaved, roleOptions }) {
  const [form, setForm] = useState({
    name: targetUser.name || "",
    title: targetUser.title || "",
    role: targetUser.role || "volunteer",
    phone: targetUser.phone || "",
    is_it_support: !!targetUser.is_it_support,
    supervisor_id: targetUser.supervisor_id || "",
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    api.get("/users")
      .then(({ data }) => setTeamMembers((data || []).filter((u) => u.active && u.id !== targetUser.id)))
      .catch(() => setTeamMembers([]));
  }, [targetUser.id]);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, supervisor_id: form.supervisor_id || null };
      await api.patch(`/users/${targetUser.id}`, payload);
      toast.success("Profile updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-md w-full" data-testid="user-edit-dialog">
        <div className="border-b border-[#E5E5E0] px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center">
              <Pencil className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <h2 className="font-display text-lg font-semibold">Edit profile</h2>
          </div>
          <button onClick={onClose} data-testid="user-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="text-sm text-[#1E3A8A]">{targetUser.email}</div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="user-edit-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Title <span className="text-xs text-[#1E3A8A] font-normal">(e.g. "Community Wellness Field Specialist — Wayne County")</span></label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="user-edit-title" placeholder="—" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Role <span className="text-xs text-[#1E3A8A] font-normal">(controls permissions)</span></label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" data-testid="user-edit-role">
              {(roleOptions && roleOptions.length ? roleOptions : FALLBACK_ROLES.map((k) => ({ key: k, name: ROLE_LABELS[k] })))
                .map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Phone</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <label className="flex items-start gap-3 p-3 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_it_support}
              onChange={(e) => setForm({ ...form, is_it_support: e.target.checked })}
              data-testid="user-edit-it-support"
              className="mt-0.5 rounded"
            />
            <span>
              <span className="block text-sm font-medium text-[#0F172A]">IT support team member</span>
              <span className="block text-xs text-[#334155] mt-0.5">Can be assigned tech support tickets and view all tickets across the team.</span>
            </span>
          </label>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">
              Supervisor <span className="text-xs text-[#1E3A8A] font-normal">(reviews this person's timesheets)</span>
            </label>
            <select
              value={form.supervisor_id}
              onChange={(e) => setForm({ ...form, supervisor_id: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              data-testid="user-edit-supervisor"
            >
              <option value="">— None (admins/ED review) —</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} {m.title ? `· ${m.title}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="user-edit-submit" className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium disabled:opacity-60">
              {submitting ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function ResetPasswordDialog({ targetUser, onClose }) {
  const [password, setPassword] = useState(generateSecurePassword());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(true);

  const regenerate = () => {
    setPassword(generateSecurePassword());
    setCopied(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/users/${targetUser.id}`, { password });
      toast.success("Password reset");
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-md w-full" data-testid="reset-password-dialog">
        <div className="border-b border-[#E5E5E0] px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center">
              <KeyRound className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <h2 className="font-display text-lg font-semibold">Reset password</h2>
          </div>
          <button onClick={onClose} data-testid="reset-close-btn"><X className="w-5 h-5" /></button>
        </div>

        {!done ? (
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-sm text-[#334155]">
              Set a new password for <strong className="text-[#0F172A]">{targetUser.name}</strong> ({targetUser.email}).
              Be sure to share it with them securely — they'll need it to sign in.
            </p>

            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">New password</label>
              <div className="flex gap-2">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setCopied(false); }}
                  data-testid="reset-password-input"
                  minLength={8}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
                />
                <button
                  type="button"
                  onClick={regenerate}
                  data-testid="reset-regen-btn"
                  className="px-3 rounded-xl border border-[#E5E5E0] text-[#1E3A8A] hover:bg-[#F9F8F6]"
                  title="Generate new password"
                >
                  <RefreshCw className="w-4 h-4" strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  onClick={copyToClipboard}
                  data-testid="reset-copy-btn"
                  className="px-3 rounded-xl border border-[#E5E5E0] text-[#1E3A8A] hover:bg-[#F9F8F6]"
                  title="Copy"
                >
                  {copied ? <Check className="w-4 h-4 text-[#1E3A8A]" /> : <Copy className="w-4 h-4" strokeWidth={1.75} />}
                </button>
              </div>
              <label className="flex items-center gap-2 mt-2 text-xs text-[#1E3A8A] cursor-pointer">
                <input type="checkbox" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} />
                Show password
              </label>
            </div>

            <div className="p-3 rounded-xl bg-[#FEF3C7] border border-[#FCD34D] text-xs text-[#92400E]">
              <strong>Security note:</strong> This password is shown once. Copy it now and share it with the team member through a secure channel. You can change it again any time.
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="reset-submit-btn"
                className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] disabled:opacity-60"
              >
                {submitting ? "Resetting…" : "Reset password"}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-6 space-y-4" data-testid="reset-success">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#E8ECE7] border border-[#7E937D]/30">
              <Check className="w-5 h-5 text-[#166534]" />
              <span className="text-sm text-[#166534] font-medium">Password updated</span>
            </div>
            <p className="text-sm text-[#334155]">
              <strong className="text-[#0F172A]">{targetUser.name}</strong> can now sign in with this password:
            </p>
            <div className="flex gap-2">
              <input
                readOnly
                value={password}
                className="flex-1 px-4 py-2.5 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] font-mono text-sm"
              />
              <button
                onClick={copyToClipboard}
                className="px-3 rounded-xl border border-[#E5E5E0] text-[#1E3A8A] hover:bg-[#F9F8F6]"
              >
                {copied ? <Check className="w-4 h-4 text-[#1E3A8A]" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-[#1E3A8A]">
              Share it with them through a secure channel (in person, encrypted message, or password manager).
              This password won't be shown again.
            </p>
            <button
              onClick={onClose}
              data-testid="reset-done-btn"
              className="w-full py-3 rounded-full bg-[#1E3A8A] text-white font-medium"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityPanel() {
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/users/activity-stats", { params: { days: windowDays } })
      .then(({ data }) => { if (!cancelled) { setData(data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { toast.error(formatApiError(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [windowDays]);

  if (loading || !data) return <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>;

  const maxActions = Math.max(1, ...data.users.map((u) => u.total_actions));

  return (
    <div className="space-y-4" data-testid="activity-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-[#334155]">
          Activity in the last <strong>{data.window_days}</strong> days. Sorted by most active.
        </div>
        <div className="inline-flex bg-white border border-[#E5E5E0] rounded-full p-1 text-xs">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              data-testid={`activity-window-${d}`}
              className={`px-3 py-1 rounded-full transition-colors ${
                windowDays === d ? "bg-[#1E3A8A] text-white" : "text-[#334155]"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F9F8F6] text-[10px] uppercase tracking-wider text-[#1E3A8A]">
              <th className="text-left px-4 py-3">Team member</th>
              <th className="text-right px-3 py-3"><span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Notes</span></th>
              <th className="text-right px-3 py-3"><span className="inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Intakes</span></th>
              <th className="text-right px-3 py-3"><span className="inline-flex items-center gap-1"><Share2 className="w-3 h-3" /> Referrals closed</span></th>
              <th className="text-right px-3 py-3"><span className="inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Appts done</span></th>
              <th className="text-left px-4 py-3 w-[26%]">Total actions</th>
              <th className="text-left px-3 py-3">Last login</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => {
              const pct = Math.round((u.total_actions / maxActions) * 100);
              return (
                <tr key={u.id} data-testid={`activity-row-${u.id}`} className="border-t border-[#F0EFEB]">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0F172A]">{u.name || u.email}</div>
                    <div className="text-[11px] text-[#1E3A8A]">{(u.role || "").replace(/_/g, " ")}</div>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#334155]">{u.notes}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#334155]">{u.intakes}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#334155]">{u.referrals_closed}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#334155]">{u.appointments_completed}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                        <div
                          className="h-full bg-[#1E3A8A]"
                          style={{ width: u.total_actions > 0 ? `${pct}%` : "0%" }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-[#0F172A] tabular-nums">{u.total_actions}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-[11px] text-[#1E3A8A]">
                    {u.last_login_at ? formatRelative(u.last_login_at) : "never"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


const VOL_STATUS_TONE = {
  pending: "bg-[#FEF3C7] text-[#92400E]",
  active: "bg-[#E8ECE7] text-[#166534]",
  rejected: "bg-[#FEE2E2] text-[#991B1B]",
  invited: "bg-[#DBEAFE] text-[#1E40AF]",
  disabled: "bg-[#FEE2E2] text-[#991B1B]",
};

function VolunteerAccountsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("active");
  const [query, setQuery] = useState("");
  const [volRoles, setVolRoles] = useState([]);
  const [showRolesModal, setShowRolesModal] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = status ? { status } : {};
    api.get("/admin/volunteers", { params })
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [status]);

  const loadRoles = () => {
    api.get("/admin/roles", { params: { scope: "volunteer" } })
      .then(({ data }) => setVolRoles(data?.roles || []))
      .catch(() => setVolRoles([]));
  };
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRoles(); }, []);

  const resetPassword = async (v) => {
    if (!window.confirm(`Send a password-reset email to ${v.name} (${v.email})?\nTheir current password keeps working until they use the link.`)) return;
    try {
      const { data } = await api.post(`/admin/volunteers/${v.id}/password-reset`);
      toast.success(`Reset link emailed · valid ${data?.expires_in_hours || 72}h`);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const disable = async (v) => {
    const reason = window.prompt(`Disable ${v.name}?\nThey will no longer be able to sign in. Optional reason (audit log):`);
    if (reason === null) return;
    try {
      await api.post(`/admin/volunteers/${v.id}/disable`, { reason: reason.trim() || null });
      toast.success(`Disabled · ${v.name}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const enable = async (v) => {
    try {
      await api.post(`/admin/volunteers/${v.id}/enable`);
      toast.success(`Re-enabled · ${v.name}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const assignRole = async (v, role_key) => {
    try {
      await api.patch(`/admin/volunteers/${v.id}/role`, { role_key });
      toast.success(`Role updated · ${v.name}`);
      // Optimistic local update so the dropdown doesn't snap back while we refetch
      setRows((prev) => prev.map((r) => (r.id === v.id ? { ...r, role_key } : r)));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = query.trim()
    ? rows.filter((v) => {
        const q = query.toLowerCase();
        return (v.name || "").toLowerCase().includes(q) || (v.email || "").toLowerCase().includes(q);
      })
    : rows;

  const roleDropdown = (v) => (
    <select
      value={v.role_key || "volunteer"}
      onChange={(e) => assignRole(v, e.target.value)}
      data-testid={`users-volunteer-role-${v.id}`}
      disabled={v.status !== "active" && v.status !== "disabled"}
      className="px-2 py-1 rounded-lg border border-[#E5E5E0] bg-white text-xs disabled:opacity-50"
    >
      {volRoles.length === 0 && <option value="volunteer">Volunteer</option>}
      {volRoles.map((r) => (
        <option key={r.key} value={r.key}>{r.name}</option>
      ))}
    </select>
  );

  return (
    <div className="space-y-4" data-testid="users-volunteers-panel">
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-3 flex items-center gap-3 flex-wrap">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search volunteer name or email…"
          data-testid="users-volunteers-search"
          className="flex-1 min-w-[180px] px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-sm"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="users-volunteers-status-filter"
          className="px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-sm"
        >
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
          <option value="invited">Invited</option>
          <option value="">All statuses</option>
        </select>
        <button
          type="button"
          onClick={() => setShowRolesModal(true)}
          data-testid="users-volunteers-manage-roles"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-xs font-medium hover:bg-[#F9F8F6]"
        >
          <ShieldCheck className="w-3 h-3" strokeWidth={2} /> Manage volunteer roles
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl h-32 animate-pulse" />
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="users-volunteers-empty">
          <HandHeart className="w-12 h-12 mx-auto text-[#1E3A8A] opacity-50 mb-3" strokeWidth={1.5} />
          <p className="text-[#334155]">No volunteer accounts match.</p>
        </div>
      ) : (
        <>
          {/* Mobile: card list */}
          <ul className="md:hidden space-y-3" data-testid="users-volunteers-list-mobile">
            {filtered.map((v) => {
              const tone = VOL_STATUS_TONE[v.status] || "bg-[#F1F5F9] text-[#475569]";
              return (
                <li key={v.id} data-testid={`users-volunteer-mobile-${v.id}`} className="bg-white border border-[#E5E5E0] rounded-2xl p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      {v.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#0F172A] truncate">{v.name}</div>
                      <div className="text-xs text-[#1E3A8A] truncate">{v.email}</div>
                      {v.phone && <div className="text-[11px] text-[#334155] truncate">{v.phone}</div>}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tone}`}>
                      {v.status}
                    </span>
                  </div>
                  {v.counties?.length > 0 && (
                    <div className="text-[11px] text-[#334155] mb-3 pb-3 border-b border-[#F0EFEB]">
                      <strong className="text-[#1E3A8A]">Counties:</strong> {v.counties.join(", ")}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 mb-3 pb-3 border-b border-[#F0EFEB]">
                    <span className="text-xs text-[#1E3A8A]">Role</span>
                    {roleDropdown(v)}
                  </div>
                  {(v.status === "active" || v.status === "disabled") && (
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => resetPassword(v)}
                        data-testid={`users-volunteer-reset-mobile-${v.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-xs font-medium text-[#1E3A8A]"
                      >
                        <KeyRound className="w-3.5 h-3.5" strokeWidth={2} /> Reset password
                      </button>
                      {v.status === "active" ? (
                        <button
                          type="button"
                          onClick={() => disable(v)}
                          data-testid={`users-volunteer-disable-mobile-${v.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#FEE2E2] text-[#991B1B] text-xs font-medium"
                        >
                          <Ban className="w-3.5 h-3.5" strokeWidth={2} /> Disable
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => enable(v)}
                          data-testid={`users-volunteer-enable-mobile-${v.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#1E3A8A] text-white text-xs font-medium"
                        >
                          <Power className="w-3.5 h-3.5" strokeWidth={2} /> Re-enable
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Desktop: table */}
          <div className="hidden md:block bg-white border border-[#E5E5E0] rounded-2xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-volunteers-table">
            <thead className="bg-[#F9F8F6] border-b border-[#E5E5E0]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Name</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden lg:table-cell">Counties</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Role</th>
                <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Status</th>
                <th className="text-right px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => {
                const tone = VOL_STATUS_TONE[v.status] || "bg-[#F1F5F9] text-[#475569]";
                return (
                  <tr key={v.id} data-testid={`users-volunteer-${v.id}`} className="border-t border-[#F0EFEB]">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0F172A]">{v.name}</div>
                      {v.phone && <div className="text-[11px] text-[#1E3A8A]">{v.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-[#334155] hidden sm:table-cell">{v.email}</td>
                    <td className="px-4 py-3 text-[#334155] hidden lg:table-cell text-xs">
                      {v.counties?.length ? v.counties.join(", ") : "—"}
                    </td>
                    <td className="px-4 py-3">{roleDropdown(v)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tone}`}>
                        {v.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(v.status === "active" || v.status === "disabled") ? (
                        <div className="inline-flex gap-2 flex-wrap justify-end">
                          <button
                            type="button"
                            onClick={() => resetPassword(v)}
                            data-testid={`users-volunteer-reset-${v.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-[11px] font-medium text-[#1E3A8A] hover:bg-[#E8ECE7] transition-colors"
                          >
                            <KeyRound className="w-3 h-3" strokeWidth={2} /> Reset password
                          </button>
                          {v.status === "active" ? (
                            <button
                              type="button"
                              onClick={() => disable(v)}
                              data-testid={`users-volunteer-disable-${v.id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-[11px] font-medium text-[#991B1B] hover:bg-[#FEE2E2] transition-colors"
                            >
                              <Ban className="w-3 h-3" strokeWidth={2} /> Disable
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => enable(v)}
                              data-testid={`users-volunteer-enable-${v.id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-[11px] font-medium hover:bg-[#172554] transition-colors"
                            >
                              <Power className="w-3 h-3" strokeWidth={2} /> Re-enable
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-[#1E3A8A]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {showRolesModal && (
        <VolunteerRolesModal
          onClose={() => setShowRolesModal(false)}
          onSaved={() => { loadRoles(); load(); }}
        />
      )}
    </div>
  );
}

function VolunteerRolesModal({ onClose, onSaved }) {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, {} = new, {...role} = edit
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get("/admin/roles", { params: { scope: "volunteer" } }),
        api.get("/permissions"),
      ]);
      setRoles(rolesRes.data?.roles || []);
      const all = permsRes.data?.permissions || [];
      setPermissions(all.filter((p) => p.scope === "volunteer"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => setEditing({ name: "", description: "", permissions: [] });
  const startEdit = (r) => setEditing({ ...r });
  const cancelEdit = () => setEditing(null);

  const togglePerm = (key) => {
    setEditing((e) => {
      const set = new Set(e.permissions || []);
      if (set.has(key)) set.delete(key); else set.add(key);
      return { ...e, permissions: Array.from(set) };
    });
  };

  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: (editing.description || "").trim() || null,
        permissions: editing.permissions || [],
      };
      if (editing.key) {
        await api.patch(`/admin/roles/${editing.key}`, payload);
        toast.success("Role updated");
      } else {
        await api.post("/admin/roles", { ...payload, scope: "volunteer" });
        toast.success("Role created");
      }
      setEditing(null);
      await load();
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (r.system) { toast.error("System roles can't be deleted"); return; }
    if (!window.confirm(`Delete role "${r.name}"? Volunteers still assigned to it must be reassigned first.`)) return;
    try {
      await api.delete(`/admin/roles/${r.key}`);
      toast.success("Role deleted");
      await load();
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-[#0F172A]/40 overflow-y-auto" data-testid="vol-roles-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E0]">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.75} />
            <div>
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">Volunteer roles</h3>
              <p className="text-xs text-[#334155]">Build the permission sets your volunteers can be assigned. Changes apply on the volunteer&apos;s next sign-in or refresh.</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="vol-roles-close" className="p-2 rounded-full hover:bg-[#F9F8F6]"><X className="w-5 h-5" /></button>
        </div>

        {editing ? (
          <div className="p-5 space-y-4" data-testid="vol-roles-editor">
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Name</label>
              <input
                value={editing.name || ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                data-testid="vol-roles-name"
                disabled={editing.system}
                placeholder="e.g., Event Lead"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm disabled:opacity-60"
              />
              {editing.system && <p className="text-[11px] text-[#1E3A8A] mt-1">System role name can&apos;t change — only the permission set.</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-[#334155] mb-1.5 uppercase tracking-wider">Description</label>
              <textarea
                value={editing.description || ""}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                data-testid="vol-roles-description"
                rows={2}
                placeholder="What does someone with this role do?"
                className="w-full px-3 py-2.5 rounded-xl border border-[#E5E5E0] bg-white text-sm"
              />
            </div>
            <div>
              <div className="text-xs font-medium text-[#334155] mb-2 uppercase tracking-wider">Permissions</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {permissions.map((p) => {
                  const checked = (editing.permissions || []).includes(p.key);
                  return (
                    <label key={p.key} className="flex items-start gap-2 p-2.5 rounded-xl border border-[#E5E5E0] bg-white hover:bg-[#F9F8F6] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePerm(p.key)}
                        data-testid={`vol-roles-perm-${p.key}`}
                        className="mt-0.5"
                      />
                      <span className="text-sm">
                        <span className="block font-medium text-[#0F172A]">{p.label}</span>
                        <span className="block text-[11px] text-[#1E3A8A]">{p.key}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={cancelEdit} className="px-4 py-2 text-sm text-[#334155]">Cancel</button>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                data-testid="vol-roles-save"
                className="px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60"
              >
                {busy ? "Saving…" : (editing.key ? "Save changes" : "Create role")}
              </button>
            </div>
          </div>
        ) : loading ? (
          <div className="p-8 text-center text-[#1E3A8A]">Loading…</div>
        ) : (
          <div className="p-5 space-y-3" data-testid="vol-roles-list">
            <ul className="space-y-2">
              {roles.map((r) => (
                <li key={r.key} data-testid={`vol-roles-row-${r.key}`} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[#E5E5E0] bg-white">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#0F172A]">{r.name}</span>
                      {r.system && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#E0E7FF] text-[#1E40AF]">System</span>}
                      <span className="text-[10px] text-[#1E3A8A]">{(r.permissions || []).length} permission{(r.permissions || []).length === 1 ? "" : "s"}</span>
                    </div>
                    {r.description && <div className="text-xs text-[#334155] mt-0.5">{r.description}</div>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => startEdit(r)} data-testid={`vol-roles-edit-${r.key}`} className="text-[11px] px-3 py-1.5 rounded-full border border-[#E5E5E0] text-[#1E3A8A] hover:bg-[#F9F8F6]">Edit</button>
                    {!r.system && (
                      <button onClick={() => remove(r)} data-testid={`vol-roles-delete-${r.key}`} className="text-[11px] px-3 py-1.5 rounded-full border border-[#E5E5E0] text-[#991B1B] hover:bg-[#FEE2E2]">Delete</button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <button onClick={startCreate} data-testid="vol-roles-new" className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]">
              <Plus className="w-4 h-4" /> New volunteer role
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

